// src/components/compliance/modals/PostureStatusModal.jsx

import React, { useMemo } from "react";
import {
  buildControlLabel,
  cleanValue,
  formatTimestamp,
  getDisplayStateLabel,
  getSummaryChipStyle,
  getTaskStatusLabel,
  normalizeText,
  safeNumber,
} from "../../../utils/complianceFormatters";
import { findTaskForItem } from "../../../utils/remediationTaskHelpers";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const STATUS_CONFIG = {
  pass: {
    eyebrow: "OASYS · PASSED CONTROLS",
    title: "Passed Controls",
    subtitle: "Controls currently reporting a passing posture state for the selected framework.",
    primaryMetric: "Passed Controls",
    tone: "ok",
    accent: "#34d399",
    actionMode: "read_only",
  },
  error: {
    eyebrow: "OASYS · ERROR CONTROLS",
    title: "Controls in Error State",
    subtitle: "Controls where the latest assessment returned an error or operational exception that needs review.",
    primaryMetric: "Error Controls",
    tone: "warn",
    accent: "#fbbf24",
    actionMode: "remediation",
  },
  unknown: {
    eyebrow: "OASYS · UNKNOWN CONTROLS",
    title: "Unknown Control State",
    subtitle: "Controls where OASYS could not determine the current posture state from the available evidence.",
    primaryMetric: "Unknown Controls",
    tone: "warn",
    accent: "#94a3b8",
    actionMode: "remediation",
  },
  fresh: {
    eyebrow: "OASYS · FRESH CONTROLS",
    title: "Fresh Evidence Controls",
    subtitle: "Controls with current evidence freshness. Use this view to verify what is healthy without expanding the table below.",
    primaryMetric: "Fresh Controls",
    tone: "ok",
    accent: "#34d399",
    actionMode: "read_only",
  },
  freshness_unknown: {
    eyebrow: "OASYS · FRESHNESS UNKNOWN",
    title: "Controls with Unknown Freshness",
    subtitle: "Controls where evidence freshness is missing, unavailable, or not yet calculated.",
    primaryMetric: "Freshness Unknown",
    tone: "warn",
    accent: "#fbbf24",
    actionMode: "remediation",
  },
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function normalizeStatusType(value) {
  const normalized = normalizeText(value || "pass");
  if (normalized === "freshnessunknown") return "freshness_unknown";
  return STATUS_CONFIG[normalized] ? normalized : "pass";
}

function buildDriftParams(row = {}, framework) {
  return {
    framework: row.framework || framework,
    controlId: row.control_id || row.controlId || undefined,
    ruleId: row.rule_id || row.ruleId || undefined,
    resourceId: row.resource_id || row.resourceId || undefined,
  };
}

function rowState(row = {}) {
  return cleanValue(row.state || row.status || row.control_state, "unknown");
}

function rowFreshness(row = {}) {
  return cleanValue(row.freshness_state || row.freshness_status, "unknown");
}

function shouldShowFixPlan(statusType, row = {}) {
  const selectedType = normalizeStatusType(statusType);
  const state = normalizeText(rowState(row));

  if (["error", "unknown", "freshness_unknown"].includes(selectedType)) return true;
  return ["fail", "failed", "error", "unknown", "non_pass", "non_passing", "non-passing"].includes(state);
}

function getRowKey(row = {}, index) {
  return [
    cleanValue(row.framework, ""),
    cleanValue(row.control_id || row.controlId, ""),
    cleanValue(row.rule_id || row.ruleId, ""),
    cleanValue(row.resource_id || row.resourceId, ""),
    index,
  ].join("::");
}

function getDetailScopeCopy(selectedType, shownCount, total) {
  if (shownCount >= total) return "All matching detail rows are loaded for this view.";

  if (["pass", "fresh"].includes(selectedType)) {
    return `Showing ${shownCount} loaded detail row${shownCount === 1 ? "" : "s"} from ${total} total controls. Use Load More to page through the remaining controls without rendering a large wall all at once.`;
  }

  return `Showing ${shownCount} loaded detail row${shownCount === 1 ? "" : "s"} from ${total} total controls.`;
}

function getCountChipLabel(shownCount, total) {
  if (shownCount >= total) return `${shownCount} shown`;
  return `${shownCount} detail rows loaded · ${total} total`;
}

function getAccentBorder(config) {
  if (config.tone === "warn") return "rgba(251, 191, 36, 0.45)";
  return "rgba(74, 222, 128, 0.42)";
}

export default function PostureStatusModal({
  open,
  framework,
  statusType = "pass",
  rows = [],
  totalCount = 0,
  activeRemediationTasks = [],
  loading = false,
  error = "",
  hasMore = false,
  onLoadMore,
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  const selectedType = normalizeStatusType(statusType);
  const config = STATUS_CONFIG[selectedType] || STATUS_CONFIG.pass;

  const visibleRows = useMemo(() => {
    return Array.isArray(rows) ? rows : [];
  }, [rows]);

  const loadedCount = visibleRows.length;
  const total = totalCount || loadedCount;
  const displayRows = visibleRows;
  const shownCount = displayRows.length;
  const effectiveHasMore = Boolean(hasMore) || shownCount < total;

  const metrics = useMemo(() => {
    const withTasks = visibleRows.filter((row) => findTaskForItem(activeRemediationTasks, row, framework)).length;
    const openFindings = visibleRows.reduce(
      (sum, row) => sum + safeNumber(row.open_findings_total || row.findings_open || row.open_findings, 0),
      0
    );
    const staleOrUnknownFreshness = visibleRows.filter((row) => {
      const freshness = normalizeText(rowFreshness(row));
      return !freshness || freshness === "unknown" || freshness.includes("stale");
    }).length;

    return { withTasks, openFindings, staleOrUnknownFreshness };
  }, [activeRemediationTasks, framework, visibleRows]);

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      ariaLabel={config.title}
      eyebrow={config.eyebrow}
      title={config.title}
      subtitle={config.subtitle}
      onClose={onClose}
      maxWidth="1120px"
      accentBorder={getAccentBorder(config)}
    >
      <div style={metricGridStyle}>
        <MetricTile label={config.primaryMetric} value={total} sub={`${framework} posture scope`} color={config.accent} />
        <MetricTile label="Rows Loaded" value={loadedCount} sub="available detail rows" color="#60a5fa" />
        <MetricTile
          label="Linked Tasks"
          value={metrics.withTasks}
          sub="tracked remediation work"
          color={metrics.withTasks > 0 ? "#fbbf24" : "#34d399"}
        />
        <MetricTile
          label="Open Findings"
          value={metrics.openFindings}
          sub="active evidence burden"
          color={metrics.openFindings > 0 ? "#f87171" : "#34d399"}
        />
      </div>

      <ModalSection
        title="Control Status Detail"
        description="Review current state, evidence freshness, linked tasks, and posture context without scrolling down to the full control table."
        badge={getCountChipLabel(shownCount, total)}
        badgeTone={config.tone}
      >
        <div
          style={{
            border: "1px solid rgba(148, 163, 184, 0.14)",
            borderRadius: 14,
            padding: "10px 12px",
            color: "#bfdbfe",
            fontSize: 13,
            lineHeight: 1.5,
            background: "rgba(15, 23, 42, 0.34)",
          }}
        >
          {loading && loadedCount === 0
            ? "Loading matching detail rows…"
            : getDetailScopeCopy(selectedType, shownCount, total)}
        </div>

        {error ? (
          <div style={{ ...getSummaryChipStyle("danger"), justifySelf: "start", borderRadius: 12 }}>
            {error}
          </div>
        ) : null}

        {loadedCount === 0 && !loading ? (
          <div className="muted" style={{ padding: 14 }}>
            No matching detail rows are currently loaded for {framework}. The KPI count may be coming from the posture score snapshot before matching detail rows are available.
          </div>
        ) : (
          displayRows.map((row, index) => {
            const controlId = row.control_id || row.controlId || "";
            const ruleId = row.rule_id || row.ruleId || "";
            const resourceId = row.resource_id || row.resourceId || "";
            const linkedTask = findTaskForItem(activeRemediationTasks, row, framework);
            const state = rowState(row);
            const freshness = rowFreshness(row);
            const openFindings = safeNumber(row.open_findings_total || row.findings_open || row.open_findings, 0);
            const canFixPlan = shouldShowFixPlan(selectedType, row);
            const freshnessTone = normalizeText(freshness).includes("stale") ? "warn" : "default";

            return (
              <TaskQueueCard
                key={getRowKey(row, index)}
                title={buildControlLabel(controlId, ruleId)}
                dueLabel={row.last_checked_at ? formatTimestamp(row.last_checked_at) : "Last check unavailable"}
                chips={[
                  { label: getDisplayStateLabel(state, "Unknown"), tone: config.tone },
                  { label: `Freshness: ${freshness}`, tone: freshnessTone, strong: false },
                  linkedTask
                    ? { label: `Task: ${getTaskStatusLabel(linkedTask.status)}`, tone: "warn", strong: false }
                    : { label: "No linked task", tone: "default", strong: false },
                ]}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: cleanValue(resourceId, "—"), wrap: true },
                ]}
                context={
                  <>
                    <strong style={{ color: "#f8fafc" }}>Open findings:</strong> {openFindings}.{" "}
                    <strong style={{ color: "#f8fafc" }}>State:</strong> {state}.{" "}
                    <strong style={{ color: "#f8fafc" }}>Freshness:</strong> {freshness}.
                    {row.reason_summary || row.remediation_hint ? (
                      <>
                        <br />
                        <strong style={{ color: "#f8fafc" }}>Context:</strong>{" "}
                        {cleanValue(row.reason_summary || row.remediation_hint, "Review the control for posture details.")}
                      </>
                    ) : null}
                  </>
                }
                contextLabel="Detail"
                actions={[
                  linkedTask
                    ? {
                        key: "review",
                        label: "Review / Manage",
                        onClick: () => onReviewTask(linkedTask, { returnTo: "posture_status" }),
                      }
                    : null,
                  canFixPlan
                    ? {
                        key: "fix-plan",
                        label: "Fix Plan",
                        onClick: () => onFixPlan(row, "control", { returnTo: "posture_status" }),
                      }
                    : null,
                  {
                    key: "guidance",
                    label: "Guidance",
                    onClick: () => onViewGuidance(row, { returnTo: "posture_status" }),
                  },
                  {
                    key: "drift",
                    label: "Open in Drift",
                    onClick: () => onOpenDrift(buildDriftParams(row, framework)),
                  },
                ]}
              />
            );
          })
        )}

        {shownCount > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              borderTop: "1px solid rgba(148, 163, 184, 0.14)",
              paddingTop: 14,
            }}
          >
            <span style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.4 }}>
              {getCountChipLabel(shownCount, total)}
            </span>

            {effectiveHasMore ? (
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={loading}
                onClick={onLoadMore}
                style={{ borderRadius: 999, whiteSpace: "nowrap" }}
              >
                {loading ? "Loading…" : "Load More"}
              </button>
            ) : (
              <span style={getSummaryChipStyle("ok")}>All loaded</span>
            )}
          </div>
        ) : null}
      </ModalSection>
    </ModalShell>
  );
}
